<script lang="ts">
	import heroImageXs from "@assets/images/fathers-family-club-hero.jpg?webp&h=1000&quality=95&imagetools";
	import heroImageSm from "@assets/images/fathers-family-club-hero.jpg?webp&h=1300&quality=95&imagetools";
	import heroImageLg from "@assets/images/fathers-family-club-hero.jpg?webp&h=1600&imagetools";
	import heroImageXl from "@assets/images/fathers-family-club-hero.jpg?webp&h=1900&imagetools";
	import heroImage3xl from "@assets/images/fathers-family-club-hero.jpg?webp&imagetools";
	import clubLogo from "@assets/images/fathers-family-club.png?webp&w=600&imagetools";
	import giftHeroImageXs from "@assets/images/gift-subscription-hero.jpg?webp&h=1000&quality=95&imagetools";
	import giftHeroImageSm from "@assets/images/gift-subscription-hero.jpg?webp&h=1300&quality=95&imagetools";
	import giftHeroImageLg from "@assets/images/gift-subscription-hero.jpg?webp&h=1600&imagetools";
	import giftHeroImageXl from "@assets/images/gift-subscription-hero.jpg?webp&h=1900&imagetools";
	import giftHeroImage3xl from "@assets/images/gift-subscription-hero.jpg?webp&imagetools";
	import giftLogoCs from "@assets/images/gift-subscription-logo-cs.png?webp&w=900&imagetools";
	import giftLogoEn from "@assets/images/gift-subscription-logo-en.png?webp&w=900&imagetools";
	import BuildYourSubscriptionButton from "@components/BuildYourSubscriptionButton.svelte";
	import GiftSubscriptionButton from "@components/GiftSubscriptionButton.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import ImageSection from "@components/sections/ImageSection.svelte";
	import { clarityEvent } from "@utils/clarity.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { Locale, type LocalizedString } from "@core/schema/Locale.js";
	import { getGiftSubscriptionPath } from "@core/schema/paths/getGiftSubscriptionPath.js";
	import { getSubscriptionPagePath } from "@core/schema/paths/getSubscriptionPagePath.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./IntroSections.translations.json" with { type: "json" };

	const halfBottomInset = tw(
		"![--pb:1.25rem] sm:![--pb:1.5rem] md:![--pb:1.75rem] lg:![--pb:2rem] xl:![--pb:2.25rem] 2xl:![--pb:2.5rem]",
	);

	const aboveActionRow = tw(
		"bottom-[calc(max(env(safe-area-inset-bottom,0),var(--pb))+7.5rem+1rem)]",
		"sm:bottom-[calc(max(env(safe-area-inset-bottom,0),var(--pb))+3.5rem+1rem)]",
	);

	const giftLogoLocaleMap: LocalizedString = {
		[Locale.cs]: giftLogoCs,
		[Locale.en]: giftLogoEn,
	};

	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);
</script>

<div class="full-width">
	<div class="grid grid-cols-1 lg:grid-cols-2 w-full bg-fcrdarkbrown">
		<ImageSection
			lcp
			class="w-full !h-[75vh] lg:!col-span-1 px-0"
			sources={{
				default: heroImageXs,
				">sm": heroImageSm,
				">lg": heroImageLg,
				">xl": heroImageXl,
				">3xl": heroImage3xl,
			}}
			innerClass={["!px-0 !gap-0", halfBottomInset]}
			darkOverlay
		>
			{#snippet heading()}
				<div class={["absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-24", aboveActionRow]}>
					<Heading level={2} text={translate("heroTitle")}>
						<Media
							width={325}
							height={305}
							sourcesets={{}}
							class="md:min-w-70"
							source={{ type: "image/webp", src: clubLogo }}
							alt={translate("heroTitle")}
						/>
					</Heading>
					<p class="uppercase text-center text-white font-inter-display font-black text-base/tighter tracking-[.17em]">
						<Formatted text={translate("heroTagline")} />
					</p>
				</div>
			{/snippet}
			{#snippet actions()}
				<BuildYourSubscriptionButton
					variant={ButtonVariant.White}
					onclick={(): void => {
						clarityEvent("hpIntroSubscriptionCtaBuild");
					}}
				/>
				<Link
					href={getSubscriptionPagePath(tenant)}
					size={ButtonSize.Medium}
					variant={ButtonVariant.OutlinedWhite}
					onclick={(): void => {
						clarityEvent("hpIntroSubscriptionCtaInfo");
					}}
				>
					<Formatted text={translate("moreInfo")} />
				</Link>
			{/snippet}
		</ImageSection>
		<ImageSection
			lcp
			class="w-full !h-[75vh] lg:!col-span-1 px-0"
			sources={{
				default: giftHeroImageXs,
				">sm": giftHeroImageSm,
				">lg": giftHeroImageLg,
				">xl": giftHeroImageXl,
				">3xl": giftHeroImage3xl,
			}}
			innerClass={["!px-0 !gap-0", halfBottomInset]}
			darkOverlay
		>
			{#snippet heading()}
				<div
					class={["absolute left-1/2 -translate-x-1/2 w-full px-6 flex flex-col items-center gap-24", aboveActionRow]}
				>
					<Heading level={2} text={translate("giftHeroTitle")}>
						<Media
							width={497}
							height={279}
							sourcesets={{}}
							class="rotate-[3.14deg]"
							source={{ type: "image/webp", src: giftLogoLocaleMap[tenant.locale] }}
							alt={translate("giftHeroTitle")}
						/>
					</Heading>
					<p class="uppercase text-center text-white font-inter-display font-black text-base/tighter tracking-[.17em]">
						<Formatted text={translate("giftHeroTagline")} />
					</p>
				</div>
			{/snippet}
			{#snippet actions()}
				<GiftSubscriptionButton
					variant={ButtonVariant.White}
					onclick={(): void => {
						clarityEvent("hpIntroGiftCtaBuild");
					}}
				/>
				<Link
					href={getGiftSubscriptionPath(tenant)}
					size={ButtonSize.Medium}
					variant={ButtonVariant.OutlinedWhite}
					onclick={(): void => {
						clarityEvent("hpIntroGiftCtaInfo");
					}}
				>
					<Formatted text={translate("moreInfo")} />
				</Link>
			{/snippet}
		</ImageSection>
	</div>
</div>
